import { DEALS_ENABLED } from '@forecastify/shared';
import { useTeams } from '../use-teams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addWireDays,
  wireMonday,
  leaveTimelineSchema,
  contractSchema,
  leaveTypeSchema,
  lineItemHasWork,
  lineItemSchema,
  overtimeTypeSchema,
  loggingViewSchema,
  resourceViewSchema,
  utilizationViewSchema,
  personSchema,
  toWireDate,
  type LeaveTimeline,
  type LeaveType,
  type LoggingView,
  type ResourceView,
  type UtilizationView,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { useSession } from '../../session';
import { useT, type Translate } from '../../i18n';
import { useWorkspaceNavigate } from '../../workspace-path';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { parseList, usePermissionSet } from '../permissions-hook';
import { useKeyedLoad } from '../use-keyed-load';
import type { PickableLineItem } from './line-item-picker';
import { useLiveInvalidation } from '../../live/use-live';
import { ResourceScreen, allocationDeletePath, allocationRefusal } from './resource-screen';
import { DASHBOARD_TOPICS, RESOURCE_TOPICS } from '../../live/topics';
import { LoggingScreen } from './logging-screen';
import {
  UtilizationScreen,
  emptyUtilization,
  type UtilizationGrouping,
} from './utilization-screen';
import { LeaveTimelineScreen, restoredLeaveWindow } from './leave-timeline-screen';
import { saveLength, useDashboardPrefs, withLength } from './dashboard-prefs';
import { RequestOverlay } from '../leave/request-overlay';
import type { RequestMode } from '../leave/request-surface';
import { lineItemContractLabel } from '../contracts/contract-label';
import { contractOptions, type ContractOptionSource } from '../contracts/contract-label';
import {
  useListMemory,
  usePruneIds,
  useRememberedBoolean,
  useRememberedString,
} from '../list-memory';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The two read-only dashboards. C49.                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The default window is the current month**, which is the question both screens are opened
 * with. The picker widens it; the day scale on the timeline follows.
 */
/**
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE LEAVE TIMELINE OPENS ON TWO WEEKS BACK AND TWELVE AHEAD — item 16.       │
 * └────────────────────────────────────────────────────────────────────┘
 *
 * It opened on THE CURRENT MONTH, and a month is the wrong frame for a question about leave in two
 * directions at once. Somebody reading this screen is asking *who is away soon* — which on the
 * 28th of the month meant a window with three days left in it — and, behind them, *who has just
 * been away*, which on the 2nd meant a window with one day of history.
 *
 * ⚠️ **Anchored on the MONDAY rather than on today**, so the bands line up with the week columns
 * the timeline draws. The `to` is the end of the twelfth week ahead, not a day twelve weeks out:
 * a window ending mid-week would draw a half column nobody can read a booking off.
 *
 * ⚠️ **The forward reach is the long one, and that is the asymmetry the screen is for.** Leave is
 * requested in advance and approved in advance; the two weeks behind are there to catch what has
 * just happened, not to report on a quarter.
 */
function leaveWindow(): { from: string; to: string } {
  const today = toWireDate(new Date());
  const monday = wireMonday(today) ?? today;
  return {
    from: addWireDays(monday, -14) ?? monday,
    to: addWireDays(monday, 12 * 7 - 1) ?? monday,
  };
}

/**
 * ⚠️ **The manager list is everybody, not a role.** Being somebody's manager is a label on a
 * person (C45, and C31's rule), so the filter offers the people who are one — which the client
 * cannot know without the roster, hence the read.
 */
function useManagers() {
  const session = useSession();
  const [managers, setManagers] = useState<readonly { id: string; name: string }[]>([]);

  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await requestAll(session, '/people').catch(() => null);
      if (response?.ok !== true) return;
      const rows = parseList(personSchema, await response.json());
      if (rows === null) return;
      const ids = new Set(
        rows.map((person) => person.managerMemberId).filter((id): id is string => id !== null),
      );
      setManagers(
        rows
          .filter((person) => ids.has(person.id))
          .map((person) => ({ id: person.id, name: person.displayName })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it refetches for ever. */
  }, [session.workspaceId]);

  return managers;
}

/**
 * ⚠️ **The project list is the workspace's own**, loaded once for the filter — the mockup filters
 * by project and the client cannot know the names without asking.
 */
function useProjects() {
  const session = useSession();
  const [projects, setProjects] = useState<readonly { id: string; name: string }[]>([]);

  useEffect(() => {
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await requestAll(session, '/projects').catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as {
        data?: { id?: string; name?: string }[];
      } | null;
      setProjects(
        (body?.data ?? [])
          .filter(
            (row): row is { id: string; name: string } =>
              typeof row.id === 'string' && typeof row.name === 'string',
          )
          .map((row) => ({ id: row.id, name: row.name })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it refetches for ever. */
  }, [session.workspaceId]);

  return projects;
}

export function LoggingDashboardRoute() {
  const session = useSession();
  const go = useWorkspaceNavigate();
  const managers = useManagers();
  const projects = useProjects();

  /**
   * ⚠️⚠️ **FOURTEEN WEEKS: NINE BACK, THIS ONE, AND FOUR AHEAD — feedback round 4 item 30.** A
   * month is the wrong frame for this screen: it is read to see who is behind and who is about to
   * be away, and both of those straddle the month's edges. The mockup's `dlDefault` was three weeks
   * back, which is barely a quarter of a reporting period — a gap from six weeks ago is exactly the
   * kind this screen exists to find, and it fell off the left edge.
   *
   * ⚠️ **The four weeks AHEAD are unchanged**, and the item said fourteen rather than where to put
   * them: forward reach answers *who is about to be away*, which is the other half of the screen
   * and was not what anybody complained about. So the extra six weeks went behind.
   */
  /* ⚠️ Computed once and kept, because the picker's *Alapértelmezett* preset resets to it — a
     value recomputed per render would move under somebody at midnight. */
  /**
   * ⚠️⚠️ **TWELVE WEEKS BACK, FOUR AHEAD — round 5 item 6.** Sixteen weeks, and the split is
   * the difference between this screen and Erőforrás: Logolás is read to find what was MISSED, so
   * its weight is behind; the planner's screen looks forward and takes the same sixteen the other
   * way round.
   *
   * ⚠️ The arithmetic: `monday - 84` is twelve Mondays back, and `+27` is the Sunday three weeks
   * after this one — this week plus three makes the four ahead. 112 days, sixteen whole weeks.
   */
  const [defaultRange] = useState(() => {
    const monday = wireMonday(toWireDate(new Date())) ?? toWireDate(new Date());
    return {
      from: addWireDays(monday, -84) ?? monday,
      to: addWireDays(monday, 27) ?? monday,
    };
  });
  /* DASH-1 · the mode and the window's LENGTH as last left, from the default's own start. */
  const dash = useDashboardPrefs('logging');
  const [window, setWindow] = useState(() => withLength(defaultRange, dash.prefs.days));
  const [mode, setMode] = useState<'week' | 'day'>(dash.prefs.mode ?? 'week');
  /* 3 Nov 2026 · the FILTERS are remembered too (`list-memory.ts`) — beside, never inside, the
     view prefs above (`forecastify.list.…`, not `forecastify.dash.…`). */
  const filterMemory = useListMemory('dashboard.logging');
  const [managerId, setManagerId] = useRememberedString(filterMemory, 'managerId');
  /* R-60 · the team filter. */
  const teams = useTeams();
  const [teamId, setTeamId] = useRememberedString(filterMemory, 'teamId');
  const [projectId, setProjectId] = useRememberedString(filterMemory, 'projectId');
  const [scheduleType, setScheduleType] = useRememberedString(filterMemory, 'scheduleType');
  const [varianceOnly, setVarianceOnly] = useRememberedBoolean(filterMemory, 'varianceOnly');
  const [query, setQuery] = useRememberedString(filterMemory, 'query');
  const [view, setView] = useState<LoggingView>({
    from: '',
    to: '',
    mode: 'week',
    columns: [],
    rows: [],
  });
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const fetchView = useCallback(
    async (isLatest: () => boolean): Promise<void> => {
      if (session.workspaceId === null) {
        setFailure({ kind: 'no-workspace' });
        return;
      }
      const search = new URLSearchParams({
        from: window.from,
        to: window.to,
        mode,
        /* ⚠️ The READER's date: the `future` verdict is the only thing that reads it, and a server in
         another timezone answers for the wrong day either side of midnight. */
        today: toWireDate(new Date()),
      });
      if (managerId !== '') search.set('managerId', managerId);
      if (teamId !== '') search.set('teamId', teamId);
      if (projectId !== '') search.set('projectId', projectId);
      if (scheduleType !== '') search.set('scheduleType', scheduleType);
      if (varianceOnly) search.set('varianceOnly', '1');
      if (query.trim() !== '') search.set('q', query.trim());

      const response = await session
        .request(`/dashboards/logging?${search.toString()}`)
        .catch(() => null);
      /* A newer request has gone out since — this answer is for a period nobody is looking at. */
      if (!isLatest()) return;
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }
      const parsed = loggingViewSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }
      if (!isLatest()) return;
      setFailure(null);
      setView(parsed.data);
    },
    [session, window, mode, managerId, teamId, projectId, scheduleType, varianceOnly, query],
  );

  /* ⚠️ A skeleton until data for THIS period and these filters lands — `use-keyed-load.ts`. The
     name search is left out on purpose: it refreshes the rows without a skeleton per keystroke. */
  const { loading, reload } = useKeyedLoad(
    JSON.stringify([
      window.from,
      window.to,
      mode,
      managerId,
      teamId,
      projectId,
      scheduleType,
      varianceOnly,
    ]),
    fetchView,
  );

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(DASHBOARD_TOPICS, reload);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <LoggingScreen
        loading={loading}
        view={view}
        range={window}
        defaultRange={defaultRange}
        managers={managers}
        projects={projects}
        managerId={managerId}
        projectId={projectId}
        scheduleType={scheduleType}
        varianceOnly={varianceOnly}
        query={query}
        onManager={setManagerId}
        teams={teams}
        teamId={teamId}
        onTeam={setTeamId}
        onProject={setProjectId}
        onScheduleType={setScheduleType}
        onVarianceOnly={setVarianceOnly}
        onQuery={setQuery}
        onMode={(next) => {
          setMode(next);
          dash.save({ mode: next });
        }}
        onRange={(from, to) => {
          setWindow({ from, to });
          saveLength(dash.save, { from, to });
        }}
        onOpenPerson={(memberId) => {
          go(`/people/${memberId}`);
        }}
        onOpenUnknown={(memberId) => {
          go(`/people/${memberId}?logs=unknown`);
        }}
      />
    </ScreenState>
  );
}

export function LeaveDashboardRoute() {
  const session = useSession();
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();
  const managers = useManagers();

  /* ⚠️ Computed once and kept, because the picker's *Alapértelmezett* preset resets to it — a
     value recomputed per render would move under somebody at midnight. */
  const [defaultRange] = useState(leaveWindow);
  /* DASH-1 · the zoom and the window's LENGTH as last left. */
  const dash = useDashboardPrefs('leave');
  const [window, setWindow] = useState(() =>
    restoredLeaveWindow(defaultRange, dash.prefs.zoom, dash.prefs.days, toWireDate(new Date())),
  );
  /* 3 Nov 2026 · the filters, remembered beside the view prefs (`list-memory.ts`). */
  const filterMemory = useListMemory('dashboard.leave');
  const [managerId, setManagerId] = useRememberedString(filterMemory, 'managerId');
  /* R-60 · the team filter. */
  const teams = useTeams();
  const [teamId, setTeamId] = useRememberedString(filterMemory, 'teamId');
  const [leaveTypeId, setLeaveTypeId] = useRememberedString(filterMemory, 'leaveTypeId');
  const [status, setStatus] = useRememberedString(filterMemory, 'status');
  const [query, setQuery] = useRememberedString(filterMemory, 'query');
  const [types, setTypes] = useState<readonly LeaveType[]>([]);
  const [timeline, setTimeline] = useState<LeaveTimeline>({
    from: '',
    to: '',
    holidays: [],
    holidayNames: [],
    rows: [],
  });
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  /* ⚠️ P3-9 · a band opens the request in the drawer — the same surface a queue row opens. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [openMode, setOpenMode] = useState<RequestMode>('drawer');

  const fetchView = useCallback(
    async (isLatest: () => boolean): Promise<void> => {
      if (session.workspaceId === null) {
        setFailure({ kind: 'no-workspace' });
        return;
      }
      const search = new URLSearchParams({ from: window.from, to: window.to });
      if (managerId !== '') search.set('managerId', managerId);
      if (teamId !== '') search.set('teamId', teamId);
      if (leaveTypeId !== '') search.set('leaveTypeId', leaveTypeId);
      if (status !== '') search.set('status', status);

      const response = await session
        .request(`/dashboards/leave?${search.toString()}`)
        .catch(() => null);
      /* A newer request has gone out since — this answer is for a period nobody is looking at. */
      if (!isLatest()) return;
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }
      const parsed = leaveTimelineSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }
      if (!isLatest()) return;
      setFailure(null);
      setTimeline(parsed.data);
    },
    [session, window, managerId, teamId, leaveTypeId, status],
  );

  /* ⚠️ Not fatal: losing it costs the type filter its options, not the screen. Read once, beside
     the timeline rather than after it — it used to wait for the timeline on every period step. */
  useEffect(() => {
    void (async () => {
      const typeResponse = await session.request('/workspace/leave/types').catch(() => null);
      if (typeResponse?.ok === true) {
        const rows = parseList(leaveTypeSchema, await typeResponse.json());
        if (rows !== null) setTypes(rows.filter((row) => row.active));
      }
    })();
  }, [session]);

  /* ⚠️ A skeleton until data for THIS period and these filters lands — `use-keyed-load.ts`. The
     name search is left out on purpose: it refreshes the rows without a skeleton per keystroke. */
  const { loading, reload } = useKeyedLoad(
    JSON.stringify([window.from, window.to, managerId, teamId, leaveTypeId, status]),
    fetchView,
  );

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(DASHBOARD_TOPICS, reload);

  /* ⚠️ The NAME filter is applied here rather than on the wire: the timeline is one workspace's
     people, and a query parameter that means *nearly* the same as the manager filter is how the
     two drift. */
  const shown = {
    ...timeline,
    rows:
      query.trim() === ''
        ? timeline.rows
        : timeline.rows.filter((row) =>
            row.memberName.toLowerCase().includes(query.trim().toLowerCase()),
          ),
  };

  return (
    <>
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <LeaveTimelineScreen
          loading={loading}
          timeline={shown}
          range={window}
          types={types}
          managers={managers}
          managerId={managerId}
          leaveTypeId={leaveTypeId}
          status={status}
          query={query}
          today={toWireDate(new Date())}
          defaultRange={defaultRange}
          onManager={setManagerId}
          teams={teams}
          teamId={teamId}
          onTeam={setTeamId}
          onLeaveType={setLeaveTypeId}
          onStatus={setStatus}
          onQuery={setQuery}
          onRange={(from, to) => {
            setWindow({ from, to });
            saveLength(dash.save, { from, to });
          }}
          {...(dash.prefs.zoom === undefined ? {} : { initialZoom: dash.prefs.zoom })}
          onZoom={(zoom) => {
            dash.save({ zoom });
          }}
          onOpenRequest={(id) => {
            setOpenId(id);
            setOpenMode('drawer');
          }}
          canOpen={(memberId) =>
            permissions.has('leave.view') ||
            memberId === session.workspaces.find((one) => one.id === session.workspaceId)?.memberId
          }
        />
      </ScreenState>

      <RequestOverlay
        requestId={openId}
        mode={openMode}
        onModeChange={setOpenMode}
        onClose={() => {
          setOpenId(null);
        }}
        onChanged={() => {
          void reload();
        }}
        onOpenFull={(identifier) => {
          go(`/requests/${identifier}`);
        }}
        onOpenTask={(key) => {
          go(`/tasks/${key}`);
        }}
      />
    </>
  );
}

/**
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ Erőforrás — C53. The one dashboard that WRITES.                          │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **AND THAT IS WHY IT RE-READS THE WHOLE WINDOW AFTER EVERY CELL.** C49's two are
 * read-only and could patch nothing; a cell here changes a person's row, the three lines above it,
 * the load verdict on every OTHER column of that row — the spread moved — and four of the six
 * figures in the strip. Patching that locally is re-implementing `distributeHours` against capacity
 * the client does not have.
 */
/**
 * ⚠️ **Two refusals with their own sentence, and everything else is *unavailable*.** The grid
 * writes, so it has refusals to explain; the rule `state-rules.md` sets is that a client branches on
 * `code`, never on `message`.
 */
function messageFor(t: Translate, code: string): string {
  if (code === 'conflict') return t('error.conflict');
  if (code === 'not_found') return t('error.not_found');
  return t('error.unavailable');
}

export function ResourceDashboardRoute() {
  const session = useSession();
  const t = useT();
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();
  const managers = useManagers();
  const projects = useProjects();

  /**
   * ⚠️ **One week back to five ahead — the mockup's own `resDefaultRange`.** A planner looks
   * FORWARD, which is the difference from Logolás: that one opens three weeks back because it is
   * read to find what was missed.
   */
  /**
   * ⚠️⚠️ **SIXTEEN WEEKS: TWO BEHIND, FOURTEEN AHEAD — round 5 item 6.** It opened on one
   * week back and five ahead, which is a planner's screen that cannot see a quarter. *"Legyen 16
   * hét előre kijelölve (2 a dátum mögött, 14 előre)."*
   *
   * ⚠️ The arithmetic: `monday - 14` is two Mondays back, and `+97` is the Sunday thirteen weeks
   * after this one — this week plus thirteen makes the fourteen ahead. 112 days, sixteen weeks.
   */
  const [defaultRange] = useState(() => {
    const monday = wireMonday(toWireDate(new Date())) ?? toWireDate(new Date());
    return {
      from: addWireDays(monday, -14) ?? monday,
      to: addWireDays(monday, 97) ?? monday,
    };
  });
  /* DASH-1 · the mode and the window's LENGTH as last left, from the default's own start. */
  const dash = useDashboardPrefs('resource');
  const [window, setWindow] = useState(() => withLength(defaultRange, dash.prefs.days));
  const [mode, setMode] = useState<'week' | 'day'>(dash.prefs.mode ?? 'week');
  /* 3 Nov 2026 · the filters, remembered beside the view prefs (`list-memory.ts`). */
  const filterMemory = useListMemory('dashboard.resource');
  const [managerId, setManagerId] = useRememberedString(filterMemory, 'managerId');
  /* R-60 · the team filter. */
  const teams = useTeams();
  const [teamId, setTeamId] = useRememberedString(filterMemory, 'teamId');
  const [projectId, setProjectId] = useRememberedString(filterMemory, 'projectId');
  const [contractId, setContractId] = useRememberedString(filterMemory, 'contractId');
  const [load, setLoad] = useRememberedString(filterMemory, 'load');
  const [overtimeOnly, setOvertimeOnly] = useRememberedBoolean(filterMemory, 'overtimeOnly');
  /* Phase 6 · DM-5 · tentative deal bookings — hidden unless asked for. */
  const [showTentative, setShowTentative] = useRememberedBoolean(filterMemory, 'showTentative');
  const [query, setQuery] = useRememberedString(filterMemory, 'query');
  const [view, setView] = useState<ResourceView>({
    from: '',
    to: '',
    mode: 'week',
    columns: [],
    rows: [],
    projectTotals: null,
    projectLineItems: null,
    projectMemberIds: null,
    stats: {
      people: 0,
      under: 0,
      over: 0,
      averageLoad: null,
      plannedHours: '0.00',
      overtimeHours: '0.00',
    },
  });
  const [contracts, setContracts] = useState<readonly ContractOptionSource[] | null>(null);
  /**
   * 3 Nov 2026 · ADAPTIVE · the contract filter offers only the chosen projects' contracts (their
   * union), named (`contractOptions`); a chosen contract outside them — or since gone — is dropped.
   */
  const offeredContracts = useMemo(() => {
    if (contracts === null) return null;
    const chosen = new Set(projectId.split(',').filter((one) => one !== ''));
    return contractOptions(
      chosen.size === 0
        ? contracts
        : contracts.filter((one) => one.projectId !== null && chosen.has(one.projectId ?? '')),
    );
  }, [contracts, projectId]);
  const offeredContractIds = useMemo(
    () => (offeredContracts === null ? null : new Set(offeredContracts.map((one) => one.value))),
    [offeredContracts],
  );
  usePruneIds(contractId, offeredContractIds, setContractId);
  const [lineItems, setLineItems] = useState<readonly PickableLineItem[]>([]);
  const [overtimeTypes, setOvertimeTypes] = useState<
    readonly { id: string; name: string; multiplier: string }[]
  >([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const fetchView = useCallback(
    async (isLatest: () => boolean): Promise<void> => {
      if (session.workspaceId === null) {
        setFailure({ kind: 'no-workspace' });
        return;
      }
      const search = new URLSearchParams({
        from: window.from,
        to: window.to,
        mode,
        /* ⚠️ The READER's date — only the *current column* tint reads it, and a server in another
         timezone highlights the wrong week either side of midnight. */
        today: toWireDate(new Date()),
      });
      if (managerId !== '') search.set('managerId', managerId);
      if (teamId !== '') search.set('teamId', teamId);
      if (projectId !== '') search.set('projectId', projectId);
      if (contractId !== '') search.set('contractId', contractId);
      if (load !== '') search.set('load', load);
      if (overtimeOnly) search.set('overtimeOnly', '1');
      if (showTentative) search.set('tentative', '1');
      if (query.trim() !== '') search.set('q', query.trim());

      const response = await session
        .request(`/dashboards/resource?${search.toString()}`)
        .catch(() => null);
      /* A newer request has gone out since — this answer is for a period nobody is looking at. */
      if (!isLatest()) return;
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }
      const parsed = resourceViewSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }
      if (!isLatest()) return;
      setFailure(null);
      setView(parsed.data);
    },
    [
      session,
      window,
      mode,
      managerId,
      teamId,
      projectId,
      contractId,
      load,
      overtimeOnly,
      showTentative,
      query,
    ],
  );

  /* ⚠️ A skeleton until data for THIS period and these filters lands — `use-keyed-load.ts`. The
     name search is left out on purpose: it refreshes the rows without a skeleton per keystroke. */
  const { loading, reload } = useKeyedLoad(
    JSON.stringify([
      window.from,
      window.to,
      mode,
      managerId,
      teamId,
      projectId,
      contractId,
      load,
      overtimeOnly,
      showTentative,
    ]),
    fetchView,
  );

  /**
   * ⚠️ **The pickers are loaded ONCE and not with the grid.** They do not depend on the window
   * or the filters, and re-reading every contract in the workspace each time somebody nudges the
   * date range would be six requests to redraw a dropdown nobody opened.
   */
  useEffect(() => {
    void (async () => {
      const response = await requestAll(session, '/contracts').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(contractSchema, await response.json());
      if (parsed === null) return;
      setContracts(
        parsed.map((one) => ({
          id: one.id,
          name: one.name,
          identifier: one.identifier,
          projectId: one.projectId,
          projectName: one.projectName,
        })),
      );
    })();
  }, [session]);

  useEffect(() => {
    void (async () => {
      /**
       * ⚠️⚠️ **OPEN LINE ITEMS ONLY, AND THE FILTER IS ON THE CLIENT BECAUSE THE ENDPOINT HAS
       * NO SUCH PARAMETER.** `/line-items` takes `contractId` and nothing else — a `?status=open`
       * would have been accepted, ignored, and left the picker offering closed items with nobody
       * the wiser. The planner must not offer one: `addItems` in the mockup filters on exactly
       * this, and the server would take the allocation regardless.
       */
      const response = await session.request('/line-items').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(lineItemSchema, await response.json());
      if (parsed === null) return;
      setLineItems(
        parsed
          /* ⚠️ U-7 · nor a licence: it takes no allocation (`line_item_takes_no_work`). */
          .filter((one) => one.status !== 'closed' && lineItemHasWork(one.type))
          .map((one) => ({
            id: one.id,
            name: one.name,
            projectId: one.projectId,
            projectName: one.projectName,
            contractLabel: lineItemContractLabel(one),
            contractName: one.contractName,
            /* ⚠️ The estimate and the window, which is how two deliverables of the same name on
               different contracts are told apart in the picker. */
            estimateHours: one.estimateHours,
            startsOn: one.startsOn,
            endsOn: one.endsOn,
          })),
      );
    })();
  }, [session]);

  /* ⚠️ The row menu's overtime entries. `worklog.read` gates the endpoint, which every planner
     has; a refusal simply leaves the menu without them rather than failing the screen. */
  useEffect(() => {
    void (async () => {
      const response = await session.request('/overtime/types').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(overtimeTypeSchema, await response.json());
      if (parsed === null) return;
      setOvertimeTypes(
        parsed
          .filter((one) => one.active)
          .map((one) => ({ id: one.id, name: one.name, multiplier: one.multiplier })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /**
   * ⚠️ **One writer for all three calls — a cell, a row patch and a delete.** They differ only in
   * the address and the method; the refusal handling and the re-read afterwards are identical, and
   * three copies of it is three places for the *no working day* branch to be forgotten.
   */
  /**
   * ⚠️⚠️ **A WRITE THAT SUCCEEDS SAYS SO, AND THIS ONE USED TO SAY NOTHING.** Every mutation on the
   * planner landed in silence: the grid redrew, and whether that was the write or a live
   * invalidation from somebody else was unknowable. `state-rules.md` §7 and the mockup's own
   * `toast(...)` on every one of these.
   *
   * ⚠️ **After `reload`, not before it.** A toast that fires on the request rather than on the
   * answer is a claim, and the one case it is wrong in is the one a reader most needs to see.
   */
  const write = async (
    path: string,
    init: RequestInit,
    /* ⚠️ The id is minted where the toast is pushed, so a caller only says what happened. */
    done?: Omit<Toast, 'id'>,
  ): Promise<void> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response === null) {
      setServerError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; details?: { code?: string; message?: string }[] };
      } | null;
      /* ⚠️ The refusals with a specific next move — a span with no working day, and P-1's
         person who is not on the project — and then the generic ones. */
      setServerError(
        allocationRefusal(t, body?.error) ?? messageFor(t, body?.error?.code ?? 'unavailable'),
      );
      return;
    }
    await reload();
    if (done !== undefined) {
      setToasts((current) => [
        ...current,
        { ...done, id: `${String(Date.now())}-${String(current.length)}` },
      ]);
    }
  };

  useLiveInvalidation(RESOURCE_TOPICS, reload);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <ResourceScreen
        loading={loading}
        view={view}
        range={window}
        defaultRange={defaultRange}
        managers={managers}
        projects={projects}
        contracts={(offeredContracts ?? []).map((one) => ({
          id: one.value,
          label: one.label,
          hint: one.hint,
        }))}
        lineItems={lineItems}
        managerId={managerId}
        projectId={projectId}
        contractId={contractId}
        load={load}
        overtimeOnly={overtimeOnly}
        query={query}
        canManage={permissions.has('project.manage')}
        serverError={serverError}
        overtimeTypes={overtimeTypes}
        onPatchItem={(item, patch) => {
          /* ⚠️ One request can carry one change, so the toast names the one that was made. */
          const title =
            patch.booking !== undefined
              ? t(patch.booking === 'soft' ? 'resource.toast.soft' : 'resource.toast.hard')
              : patch.distribution !== undefined
                ? t('resource.toast.priority')
                : t(
                    patch.overtimeTypeId === null
                      ? 'resource.toast.overtimeOff'
                      : 'resource.toast.overtimeOn',
                  );
          void write(
            `/allocations/${item.allocationId}`,
            { method: 'PATCH', body: JSON.stringify(patch) },
            { title, description: item.lineItemName },
          );
        }}
        onDeleteItem={(item, from) => {
          /* ⚠️ F4.1 · from the member's today on — the past stays. */
          void write(
            allocationDeletePath(item.allocationId, from),
            { method: 'DELETE' },
            { title: t('resource.toast.removed'), description: item.lineItemName },
          );
        }}
        onManager={setManagerId}
        teams={teams}
        teamId={teamId}
        onTeam={setTeamId}
        onProject={setProjectId}
        onContract={setContractId}
        onLoad={setLoad}
        onOvertimeOnly={setOvertimeOnly}
        showTentative={showTentative}
        {...(DEALS_ENABLED && permissions.has('deal.view')
          ? { onShowTentative: setShowTentative }
          : {})}
        onQuery={setQuery}
        onMode={(next) => {
          setMode(next);
          dash.save({ mode: next });
        }}
        onRange={(from, to) => {
          setWindow({ from, to });
          saveLength(dash.save, { from, to });
        }}
        onOpenPerson={(memberId) => {
          go(`/people/${memberId}`);
        }}
        onWrite={(input) => {
          void write(
            '/allocations',
            { method: 'POST', body: JSON.stringify(input) },
            /* ⚠️ Zero CLEARS a column, and the two are worth telling apart in the corner of the
               screen — a mistyped cell that reads as *saved* is a plan quietly emptied. */
            { title: t(input.hours > 0 ? 'resource.toast.saved' : 'resource.toast.cleared') },
          );
        }}
        onWriteMany={(writes) => {
          /* ⚠️ One request and one transaction for the whole range — see `saveMany`. */
          const columns = new Set(writes.map((one) => one.fromDate)).size;
          void write(
            '/allocations/batch',
            { method: 'POST', body: JSON.stringify({ writes }) },
            {
              title: t(
                writes.some((one) => one.hours > 0)
                  ? 'resource.toast.copied'
                  : 'resource.toast.clearedRange',
                { count: columns },
              ),
            },
          );
        }}
      />

      <Toaster
        toasts={[...toasts]}
        label={t('resource.toasts')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
        dismissLabel={t('common.close')}
      />
    </ScreenState>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Utilizáció. How much of everybody's time is booked.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Twelve weeks from this Monday, or two weeks in the daily view** — the mockup's own `def`.
 * Switching the view resets the window to that view's default, as the mockup's `utRange: null`
 * does: a twelve-week window of day columns is eighty-four of them.
 *
 * ⚠️ **The grouping is not a request.** It rearranges the people the answer already holds, so it
 * lives in the screen's state and not in the fetch key.
 */
function utilizationDefault(mode: 'week' | 'day'): { from: string; to: string } {
  const monday = wireMonday(toWireDate(new Date())) ?? toWireDate(new Date());
  return { from: monday, to: addWireDays(monday, mode === 'day' ? 13 : 83) ?? monday };
}

export function UtilizationDashboardRoute() {
  const session = useSession();
  const go = useWorkspaceNavigate();
  /* DASH-1 · the mode and the window's LENGTH as last left, from that mode's default start. */
  const dash = useDashboardPrefs('utilization');
  const [mode, setMode] = useState<'week' | 'day'>(dash.prefs.mode ?? 'week');
  const [window, setWindow] = useState(() =>
    withLength(utilizationDefault(dash.prefs.mode ?? 'week'), dash.prefs.days),
  );
  const [grouping, setGrouping] = useState<UtilizationGrouping>('role');
  /* 3 Nov 2026 · the filters, remembered beside the view prefs (`list-memory.ts`). */
  const filterMemory = useListMemory('dashboard.utilization');
  const [roleId, setRoleId] = useRememberedString(filterMemory, 'roleId');
  const [seniority, setSeniority] = useRememberedString(filterMemory, 'seniority');
  const [projectId, setProjectId] = useRememberedString(filterMemory, 'projectId');
  /* R-60 · the team filter. */
  const teams = useTeams();
  const [teamId, setTeamId] = useRememberedString(filterMemory, 'teamId');
  const [query, setQuery] = useRememberedString(filterMemory, 'query');
  const [view, setView] = useState<UtilizationView>(emptyUtilization);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const fetchView = useCallback(
    async (isLatest: () => boolean): Promise<void> => {
      if (session.workspaceId === null) {
        setFailure({ kind: 'no-workspace' });
        return;
      }
      const search = new URLSearchParams({
        from: window.from,
        to: window.to,
        mode,
        today: toWireDate(new Date()),
      });
      if (roleId !== '') search.set('roleId', roleId);
      if (seniority !== '') search.set('seniority', seniority);
      if (projectId !== '') search.set('projectId', projectId);
      if (teamId !== '') search.set('teamId', teamId);
      if (query.trim() !== '') search.set('q', query.trim());

      const response = await session
        .request(`/dashboards/utilization?${search.toString()}`)
        .catch(() => null);
      if (!isLatest()) return;
      if (response === null) {
        setFailure({ kind: 'error', code: 'unavailable' });
        return;
      }
      if (!response.ok) {
        setFailure(await failureOf(response));
        return;
      }
      const parsed = utilizationViewSchema.safeParse(await response.json());
      if (!parsed.success) {
        setFailure({ kind: 'error', code: 'validation_failed' });
        return;
      }
      if (!isLatest()) return;
      setFailure(null);
      setView(parsed.data);
    },
    [session, window, mode, roleId, seniority, projectId, teamId, query],
  );

  /* ⚠️ The search is left out of the key, as on Erőforrás: it refreshes without a skeleton. */
  const { loading, reload } = useKeyedLoad(
    JSON.stringify([window.from, window.to, mode, roleId, seniority, projectId, teamId]),
    fetchView,
  );

  useLiveInvalidation(RESOURCE_TOPICS, reload);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <UtilizationScreen
        loading={loading}
        view={view}
        range={window}
        defaultRange={utilizationDefault(mode)}
        grouping={grouping}
        roleId={roleId}
        seniority={seniority}
        projectId={projectId}
        query={query}
        onGrouping={setGrouping}
        onRole={setRoleId}
        onSeniority={setSeniority}
        onProject={setProjectId}
        teams={teams}
        teamId={teamId}
        onTeam={setTeamId}
        onQuery={setQuery}
        onMode={(next) => {
          setMode(next);
          const reset = utilizationDefault(next);
          setWindow(reset);
          dash.save({ mode: next });
          saveLength(dash.save, reset);
        }}
        onRange={(range) => {
          setWindow({ from: range.from, to: range.to });
          saveLength(dash.save, range);
        }}
        onClear={() => {
          setRoleId('');
          setSeniority('');
          setProjectId('');
          setTeamId('');
          setQuery('');
        }}
        onOpenPerson={(memberId) => {
          go(`/people/${memberId}`);
        }}
      />
    </ScreenState>
  );
}
